import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsLockFill, BsTelephone } from 'react-icons/bs';

import AddonActivateModal from '#globalComponents/addons/AddonActivateModal';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { PHONE_ADDON } from '#lib/addons/addonConfigs';

import { contactPeopleKeys } from '../../models/contactPeople/keys';

const PHONE_CAPABILITY = `${PHONE_ADDON.key}.access`;

const KIND_LABELS = { mobile: 'Mobil', direct: 'Durchwahl', switchboard: 'Zentrale', general: 'Telefon' };

/** Mask for a contact the vendor holds a number for that nobody has revealed yet. */
const GENERIC_MASK = '+49 1•• •••• •••';

/**
 * Whether a contact has a number the org could unlock: a masked pool number or a vendor that
 * holds one.
 * @param {object} contact
 * @returns {boolean}
 */
export const hasLockedPhone = (contact) => Boolean(contact?.phoneTeaser) || contact?.phoneAvailability === 'available';

/**
 * Phone lines of a contact row. With the phone add-on it shows every number of the person with
 * its kind, one per line, in the order the backend ranks them; without it, a contact with an
 * unlockable number shows a masked preview that opens the add-on modal. The real numbers never
 * reach the browser without the capability.
 */
const ContactPhone = ({ contact, lockedCount, className }) => {
	const { hasCapability } = useCapabilityCheck(PHONE_CAPABILITY);
	const queryClient = useQueryClient();
	const [modalOpen, setModalOpen] = useState(false);

	const legacyPhone = contact.phoneNumber ? [{ value: contact.phoneNumber, kind: 'general' }] : [];
	const phones = contact.phones?.length ? contact.phones : legacyPhone;

	if (phones.length > 0) {
		return (
			<span className={`flex flex-col gap-0.5 ${className}`}>
				{phones.map((phone) => (
					<span key={phone.value} className="text-xs text-text flex items-center gap-1">
						<BsTelephone className="w-3 h-3 flex-shrink-0" />
						<span className="truncate">{phone.displayValue ?? phone.value}</span>
						<span className="text-bgBase-800">({KIND_LABELS[phone.kind] ?? 'Telefon'})</span>
					</span>
				))}
			</span>
		);
	}

	if (!hasCapability && hasLockedPhone(contact)) {
		const masked = contact.phoneTeaser?.masked ?? GENERIC_MASK;
		const kind = KIND_LABELS[contact.phoneTeaser?.kind ?? 'mobile'];
		const highlight =
			lockedCount > 1
				? `${lockedCount} Ansprechpartner in dieser Liste haben eine Telefonnummer, die Sie freischalten können.`
				: 'Für diesen Ansprechpartner liegt eine Telefonnummer vor.';
		return (
			<>
				<button
					type="button"
					onClick={(event) => {
						event.stopPropagation();
						setModalOpen(true);
					}}
					className={`text-xs flex items-center gap-1 group ${className}`}
					title="Telefonnummer freischalten"
				>
					<BsLockFill className="w-3 h-3 flex-shrink-0 text-bgBase-800" />
					<span className="tracking-wide text-text">{masked}</span>
					<span className="text-bgBase-800">({kind})</span>
					<span className="text-primary-600 group-hover:text-primary-700 font-medium underline underline-offset-2">
						Freischalten
					</span>
				</button>
				{/* The modal portals to body, but React still bubbles its clicks through this row,
				    which is itself a toggle button. */}
				<span onClick={(event) => event.stopPropagation()}>
					<AddonActivateModal
						addon={PHONE_ADDON}
						isOpen={modalOpen}
						onClose={() => setModalOpen(false)}
						title="Telefonnummern freischalten"
						highlight={highlight}
						benefits={PHONE_ADDON.benefits}
						onActivated={() => queryClient.invalidateQueries({ queryKey: contactPeopleKeys.all })}
					/>
				</span>
			</>
		);
	}

	return null;
};

ContactPhone.propTypes = {
	contact: PropTypes.shape({
		phoneNumber: PropTypes.string,
		phoneAvailability: PropTypes.string,
		phoneTeaser: PropTypes.shape({
			masked: PropTypes.string.isRequired,
			kind: PropTypes.string.isRequired,
		}),
		phones: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.string.isRequired,
				displayValue: PropTypes.string,
				kind: PropTypes.string.isRequired,
			}),
		),
	}).isRequired,
	lockedCount: PropTypes.number,
	className: PropTypes.string,
};

ContactPhone.defaultProps = {
	lockedCount: 0,
	className: '',
};

export default ContactPhone;
